import Localizer from "@/localization/Localizer";
import {Button, ButtonType, NumberInput, NumberInputBehaviour} from "@renta-apps/athenaeum-react-components";
import React, { useState } from "react";

import styles from "./AddToCartButton.module.scss";

export interface IBaseAddToCartButtonProps {
    showNumberInput?: boolean;

    showAddToCartButton?: boolean;

    showRemoveFromCartButton?: boolean;

    /**
     * If button is shown it will hide the text and icon will be left.
     */
    hideButtonText?: boolean;
    
    /**
     * If set to true, number of products in the shopping cart is overriddren; furthermore for such state "add to cart" button is not presented (number of products in the shopping cart is changed by using the "-" and the "+" buttons
     * If set to false, adds selected number of products to the number of products in the shopping cart; the selected number of products is set by clicking on the "+" or the "-" buttons and needs confirmation by clicking on the "add to cart" button
     */
    overrideCount?: boolean;
}

export interface IAddToCartButtonProps extends IBaseAddToCartButtonProps {
    /**
     * Represents number of products
     */
    count: number;

    /**
     * Called when the user clicks the {@link AddToCartButton} in its Button-state, its plus or minus buttons in its NumberInput state, or sets the value of the NumberInput.
     */
    onCountChange(newCount: number): Promise<void>;

    className?: string;

    /**
     * Can the user click the button or change the count.
     */
    disabled?: boolean;
}

const AddToCartButton: React.FC<IAddToCartButtonProps> = (props: IAddToCartButtonProps) => {
    const [count, setCount] = useState(1);

    const deleteFromShoppingCartAsync = async (): Promise<void> => {
        await props.onCountChange(0);
    }
    
    return (
        <div className={`${props.className} ${styles.addToCartButton} ${props.showAddToCartButton ? 'flex-1 d-flex flex-column align-items-end justify-content-end gap-2' : ''}`}>
            {
                (props.showNumberInput) &&
                (
                    <NumberInput
                        className={`renta-easy-number-input ${styles.numberInput}`}
                        min={1}
                        max={100000}
                        value={props.overrideCount ? props.count : count}
                        behaviour={NumberInputBehaviour.Restricted}
                        increaseIcon="plus"
                        decreaseIcon="minus"
                        readonly={props.disabled}
                        onChange={async (_, newCount) => props.overrideCount ? await props.onCountChange(newCount) : setCount(newCount)}
                        disableButtonsWhenCannotChangeValue
                    />
                )
            }
            {
                (props.showRemoveFromCartButton) &&
                (
                    <Button 
                        type={ButtonType.Danger}
                        icon={{name: "trash"}}
                        title={Localizer.shoppingCartPageRemoveProductTitle}
                        onClick={async () => await deleteFromShoppingCartAsync()}
                        confirm={Localizer.shoppingCartPageRemoveProductConfirm}
                        className={styles.removeButton}
                    />
                )
            }

            {
                (props.showAddToCartButton) &&
                (
                    <Button
                        id={"addToCartButton"}
                        className={styles.button}
                        label={props.hideButtonText ? "" : Localizer.addToCartText}
                        icon={{name: "shopping-cart"}}
                        type={ButtonType.Orange}
                        disabled={props.disabled}
                        onClick={async () => await props.onCountChange(count)}
                    />
                )
            }
        </div>);
}

export default AddToCartButton;